import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import PropTypes from "prop-types";

const SDK_WAIT_TIMEOUT_MS = 8000;
const SDK_POLL_INTERVAL_MS = 100;

const waitForSDK = () =>
  new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }
    const start = Date.now();
    const tick = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }
      if (Date.now() - start >= SDK_WAIT_TIMEOUT_MS) {
        reject(new Error("Turnstile SDK failed to load"));
        return;
      }
      window.setTimeout(tick, SDK_POLL_INTERVAL_MS);
    };
    tick();
  });

const TurnstileWidget = forwardRef(function TurnstileWidget(
  { siteKey, onToken, onExpire, onError, className },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const sdkRef = useRef(null);

  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onExpireRef.current = onExpire;
  onErrorRef.current = onError;

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        if (sdkRef.current && widgetIdRef.current) {
          try {
            sdkRef.current.reset(widgetIdRef.current);
          } catch {
            /* widget may have been removed */
          }
        }
      },
    }),
    []
  );

  useEffect(() => {
    if (!siteKey) {
      onErrorRef.current?.("missing-sitekey");
      return;
    }
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;

    waitForSDK()
      .then((sdk) => {
        if (cancelled || !containerRef.current) return;
        sdkRef.current = sdk;
        widgetIdRef.current = sdk.render(containerRef.current, {
          sitekey: siteKey,
          appearance: "interaction-only",
          "refresh-expired": "auto",
          retry: "auto",
          callback: (token) => onTokenRef.current?.(token),
          "expired-callback": () => onExpireRef.current?.(),
          "error-callback": (code) => onErrorRef.current?.(code),
        });
      })
      .catch((err) => {
        if (!cancelled) onErrorRef.current?.(err.message);
      });

    return () => {
      cancelled = true;
      const sdk = sdkRef.current;
      const id = widgetIdRef.current;
      if (sdk && id) {
        try {
          sdk.remove(id);
        } catch {
          /* widget may have already been torn down */
        }
      }
      sdkRef.current = null;
      widgetIdRef.current = null;
    };
  }, [siteKey]);

  return <div ref={containerRef} className={className} />;
});

TurnstileWidget.propTypes = {
  siteKey: PropTypes.string.isRequired,
  onToken: PropTypes.func.isRequired,
  onExpire: PropTypes.func,
  onError: PropTypes.func,
  className: PropTypes.string,
};

export default TurnstileWidget;
